// packages/core/src/progress/LessonProgress.ts
// ─────────────────────────────────────────────────────────────────────────
// LESSON PROGRESS — the model + the status ladder. Single source of truth.
//
// THE LADDER (locked product decision):
//   1. Started    = startedAt
//   2. Read       = readAt        ← EARNED read-through signal (rewardable)
//   3. Listened   = listenedAt    ← EARNED listen signal (rewardable)
//   4. Completed  = completedAt OR readAt OR listenedAt
//   5. Reviewed   = quizPassedAt
//   6. Mastered   = flashcardsMasteredAt OR retainedMasteryAt
//
// ── COMPLETION vs REWARD (the load-bearing distinction) ────────────────────
// Completion is reached three ways, and NOT all three earn a reward:
//
//   • self_attested  → user clicked "Finish lesson". Stamps completedAt ONLY.
//                      Unlocks Mastery + counts as done for navigation and the
//                      module rollups, but banks NO XP/gems.
//   • read_depth     → user reached the final content block. Stamps completedAt
//                      AND readAt. readAt is the rewardable read signal.
//   • listen_90 /    → user listened to 90% / the end. Stamps completedAt AND
//     audio_ended       listenedAt. listenedAt is the rewardable listen signal.
//
// WHY completedAt exists (do not "simplify" it away):
//   The old model overloaded readAt as { complete + legacy-boolean + reward
//   trigger }. That was survivable while the only finish path was the old Next
//   click. Once "Finish lesson" becomes an explicit button, stamping readAt on
//   it would let users FARM read XP without reading. completedAt decouples
//   "the lesson is done" from "the user earned the read reward".
//
//   ⚠️ NEVER stamp readAt from a self-attested finish. That reopens click-
//   farming. markCompletion enforces this: only read_depth writes readAt.
//
// LEGACY BOOLEAN BRIDGE (nis.lesson.{m}.{l}):
//   The derived boolean is completed(record) = completedAt || readAt ||
//   listenedAt. It is recomputed on EVERY write and never written independently,
//   so a self-attested finish (completedAt only) flips it true exactly like
//   reading or listening does — keeping the six existing Learn views correct.
//
// REWARD ELIGIBILITY stays STRICTER than completion:
//   isReadRewardEarned   = Boolean(readAt)
//   isListenRewardEarned = Boolean(listenedAt)
//   Completion alone (self_attested) earns neither.
// ─────────────────────────────────────────────────────────────────────────

import { type ProgressStore, progressKey, legacyKey } from './ProgressStore';

// ── Completion source ─────────────────────────────────────────────────────
// Records HOW a lesson was completed, so future server authority can validate
// each path differently (self_attested trusted for progress, not reward;
// consumption events trusted for both).
export type CompletionSource =
	| 'self_attested' // Finish lesson button — progress only, no reward
	| 'read_depth' // reached final content block — full read reward
	| 'listen_90' // crossed 90% of audio — full listen reward
	| 'audio_ended'; // reached audio end — full listen reward

// ── Record ────────────────────────────────────────────────────────────────
// `completedAt` + `completionSource` are new. All other fields carry over
// verbatim so existing writers + the migration need no shape change.

export interface LessonProgress {
	lessonId: string;
	moduleId: string;
	startedAt?: string;
	readAt?: string; // EARNED read signal (rewardable)
	listenedAt?: string; // EARNED listen signal (rewardable)
	completedAt?: string; // completion for unlock/nav/progress (may be reward-less)
	completionSource?: CompletionSource;
	appliedAt?: string; // DORMANT
	quizPassedAt?: string;
	flashcardsMasteredAt?: string;
	retainedMasteryAt?: string; // DORMANT (SRS retention)
	masteredAt?: string; // DORMANT (legacy umbrella; prefer the two above)
	revision?: number;
}

/** Phase keys that markPhase can stamp (timestamped milestones only). */
export type LessonPhase = Exclude<
	keyof LessonProgress,
	'lessonId' | 'moduleId' | 'revision' | 'completionSource'
>;

function nowISO(): string {
	return new Date().toISOString();
}

// ── Pure status derivation (no storage) ────────────────────────────────────

export function isStarted(p: LessonProgress): boolean {
	return Boolean(
		p.startedAt ||
			p.readAt ||
			p.listenedAt ||
			p.completedAt ||
			p.quizPassedAt,
	);
}

export function isRead(p: LessonProgress): boolean {
	return Boolean(p.readAt);
}

export function isListened(p: LessonProgress): boolean {
	return Boolean(p.listenedAt);
}

/** LOCKED: completion = self-attested OR consumed by reading OR listening. */
export function isCompleted(p: LessonProgress): boolean {
	return Boolean(p.completedAt || p.readAt || p.listenedAt);
}

// ── Reward eligibility — STRICTER than completion ──────────────────────────
// A self-attested finish (completedAt only) is NOT reward-eligible. Only a real
// read/listen signal is. The dispatcher stays keyed on these, never isCompleted.

export function isReadRewardEarned(p: LessonProgress): boolean {
	return Boolean(p.readAt);
}

export function isListenRewardEarned(p: LessonProgress): boolean {
	return Boolean(p.listenedAt);
}

export function isReviewed(p: LessonProgress): boolean {
	return Boolean(p.quizPassedAt);
}

export function isMastered(p: LessonProgress): boolean {
	return Boolean(
		p.flashcardsMasteredAt || p.retainedMasteryAt || p.masteredAt,
	);
}

export function isInProgress(p: LessonProgress): boolean {
	return isStarted(p) && !isCompleted(p);
}

export interface LessonProgressFlags {
	started: boolean;
	read: boolean;
	listened: boolean;
	completed: boolean;
	reviewed: boolean;
	mastered: boolean;
	inProgress: boolean;
}

export function progressFlags(p: LessonProgress): LessonProgressFlags {
	return {
		started: isStarted(p),
		read: isRead(p),
		listened: isListened(p),
		completed: isCompleted(p),
		reviewed: isReviewed(p),
		mastered: isMastered(p),
		inProgress: isInProgress(p),
	};
}

export type CoarseStatus = 'not_started' | 'in_progress' | 'completed';

export function coarseStatus(p: LessonProgress): CoarseStatus {
	if (isCompleted(p)) return 'completed';
	if (isStarted(p)) return 'in_progress';
	return 'not_started';
}

// ── Store-bound reads/writes ────────────────────────────────────────────────

function emptyRecord(moduleId: string, lessonId: string): LessonProgress {
	return { lessonId, moduleId };
}

/**
 * Read the rich record. If absent but the legacy boolean is set, synthesize a
 * migrated record and persist it once.
 *
 * ⚠️ ONE-WAY MIGRATION: an old boolean-only completion was NEVER a proven read.
 * We migrate it to `completedAt` (source 'self_attested'), NOT `readAt`.
 * Backfilling readAt would retroactively grant read-reward eligibility to every
 * historically-complete lesson. completedAt preserves "done" without minting
 * reward signal. No rewards re-fire either way (the ledger is separate).
 */
export function readLessonProgress(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
): LessonProgress {
	const raw = store.get(progressKey(moduleId, lessonId));
	if (raw) {
		try {
			return JSON.parse(raw) as LessonProgress;
		} catch {
			/* fall through to bridge / empty */
		}
	}

	if (store.get(legacyKey(moduleId, lessonId)) === 'true') {
		const migrated: LessonProgress = {
			...emptyRecord(moduleId, lessonId),
			startedAt: nowISO(),
			completedAt: nowISO(),
			completionSource: 'self_attested',
		};
		writeLessonProgress(store, migrated);
		return migrated;
	}

	return emptyRecord(moduleId, lessonId);
}

/**
 * Persist a record AND recompute the derived legacy boolean from it.
 *
 * THE BRIDGE IS THREE-WAY: true iff completed(record) = completedAt || readAt
 * || listenedAt. This is the change that makes a self-attested finish (which
 * writes completedAt only) flip the legacy boolean, so the module map + every
 * boolean reader treats it as done. Reward eligibility is unaffected — it reads
 * readAt/listenedAt directly, never this boolean.
 */
export function writeLessonProgress(
	store: ProgressStore,
	p: LessonProgress,
): void {
	store.set(progressKey(p.moduleId, p.lessonId), JSON.stringify(p));

	// Derived legacy boolean — true iff completed (self-attested OR read OR listen).
	const lk = legacyKey(p.moduleId, p.lessonId);
	if (isCompleted(p)) {
		store.set(lk, 'true');
	} else {
		store.remove(lk);
	}
}

/**
 * Stamp a single phase if unset. Returns { progress, transitioned }; transitioned
 * is true only on the unset→set edge (exactly when a reward dispatcher should
 * bank). Idempotent. Used for the earned signals (readAt/listenedAt) and for
 * startedAt; completion goes through markCompletion below.
 */
export function markLessonPhase(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
	phase: LessonPhase,
): { progress: LessonProgress; transitioned: boolean } {
	const current = readLessonProgress(store, moduleId, lessonId);
	if (current[phase]) {
		return { progress: current, transitioned: false };
	}
	const next: LessonProgress = {
		...current,
		startedAt: current.startedAt ?? nowISO(),
		[phase]: nowISO(),
	};
	writeLessonProgress(store, next);
	return { progress: next, transitioned: true };
}

/**
 * Mark a lesson complete via a specific source. THE single entry point for
 * completion. Returns { progress, transitioned }, where `transitioned` is the
 * completedAt unset→set edge — fire the Mastery-unlock / completion banner
 * exactly once on that edge.
 *
 * Phase stamping by source (enforced here so callers can't get it wrong):
 *   • self_attested → completedAt only. NEVER readAt/listenedAt.
 *   • read_depth    → completedAt + readAt (readAt is the rewardable signal).
 *   • listen_90 /   → completedAt + listenedAt.
 *     audio_ended
 *
 * Idempotency of the underlying phases: completedAt is set once (first source
 * wins for the "transitioned" edge), but a later stronger signal still stamps
 * its earned phase. E.g. self-attested finish (completedAt), THEN the user
 * scrolls to the end (read_depth) → readAt gets stamped so the read reward can
 * still be earned, while completedAt/completionSource keep their original value.
 * The caller decides whether to dispatch the read/listen reward based on the
 * earned-phase transition it observes (see below).
 *
 * The returned `readTransitioned` / `listenTransitioned` flags tell the caller
 * whether THIS call newly stamped an earned signal, so it can dispatch the
 * corresponding reward event once. (completedAt's own edge is `transitioned`.)
 */
export interface MarkCompletionResult {
	progress: LessonProgress;
	/** completedAt just went unset→set (fire unlock/banner once). */
	transitioned: boolean;
	/** readAt just went unset→set this call (dispatch lesson_read once). */
	readTransitioned: boolean;
	/** listenedAt just went unset→set this call (dispatch lesson_listened once). */
	listenTransitioned: boolean;
}

export function markCompletion(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
	source: CompletionSource,
): MarkCompletionResult {
	const current = readLessonProgress(store, moduleId, lessonId);

	const willSetCompleted = !current.completedAt;
	const willSetRead = source === 'read_depth' && !current.readAt;
	const willSetListen =
		(source === 'listen_90' || source === 'audio_ended') &&
		!current.listenedAt;

	// Nothing new to stamp → fully idempotent no-op (report no transitions).
	if (!willSetCompleted && !willSetRead && !willSetListen) {
		return {
			progress: current,
			transitioned: false,
			readTransitioned: false,
			listenTransitioned: false,
		};
	}

	const next: LessonProgress = {
		...current,
		startedAt: current.startedAt ?? nowISO(),
	};

	if (willSetCompleted) {
		next.completedAt = nowISO();
		next.completionSource = source; // first source to complete wins the tag
	}
	if (willSetRead) next.readAt = nowISO();
	if (willSetListen) next.listenedAt = nowISO();

	writeLessonProgress(store, next);

	return {
		progress: next,
		transitioned: willSetCompleted,
		readTransitioned: willSetRead,
		listenTransitioned: willSetListen,
	};
}

export function isLessonPhaseDone(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
	phase: LessonPhase,
): boolean {
	return Boolean(readLessonProgress(store, moduleId, lessonId)[phase]);
}

/**
 * Compatibility shim for the old boolean-first API.
 *
 * `done = true` now stamps a self-attested completion (completedAt) — the
 * historical meaning of "mark complete" was "this lesson is done", which maps
 * to completion, NOT to an earned read. (Old callers that truly meant "the user
 * read this" should migrate to markCompletion('read_depth').)
 *
 * `done = false` clears ALL THREE completion contributors so the derived boolean
 * goes false.
 */
export function setLessonComplete(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
	done = true,
): void {
	if (done) {
		markCompletion(store, moduleId, lessonId, 'self_attested');
		return;
	}
	const current = readLessonProgress(store, moduleId, lessonId);
	const cleared: LessonProgress = { ...current };
	delete cleared.readAt;
	delete cleared.listenedAt;
	delete cleared.completedAt;
	delete cleared.completionSource;
	writeLessonProgress(store, cleared);
}

export function isLessonComplete(
	store: ProgressStore,
	moduleId: string,
	lessonId: string,
): boolean {
	return isCompleted(readLessonProgress(store, moduleId, lessonId));
}
